import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../App';
import Navigation from '../components/Navigation';
import { CheckCircle, Loader } from 'lucide-react';

const VirtualCorralSuccess = ({ user, logout }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setMessage('No payment session found');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, currentAttempt = 0) => {
    const maxAttempts = 5;
    
    if (currentAttempt >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. Please check your email for confirmation or contact support.');
      return;
    }

    try {
      const response = await api.get(`/subscriptions/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! Your Virtual Corral subscription has been activated.');
        setTimeout(() => {
          navigate('/virtual-corral');
        }, 3000);
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
      } else {
        // Payment still processing, continue polling
        setAttempts(currentAttempt + 1);
        setTimeout(() => {
          pollPaymentStatus(sessionId, currentAttempt + 1);
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      setMessage('Error verifying payment. Please contact support if you were charged.');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FCFCF8' }}>
      <Navigation user={user} logout={logout} />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: '#6FCFD3' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#372F2F' }}>Processing Payment</h2>
              <p className="text-gray-600">{message}</p>
              {attempts > 0 && (
                <p className="text-sm text-gray-500 mt-2">Attempt {attempts} of 5...</p>
              )}
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#6FCFD3' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#D66238' }}>Payment Successful!</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">Redirecting you to Virtual Corral...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-100">
                <span className="text-red-600 text-3xl">✕</span>
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#D66238' }}>Payment Issue</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <button
                onClick={() => navigate('/virtual-corral')}
                className="px-6 py-2 rounded text-white"
                style={{ backgroundColor: '#6FCFD3' }}
              >
                Return to Virtual Corral
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VirtualCorralSuccess;
